import { FilterName } from "@faire/deprecated-retailer-visitor-shared/components/Filters/FilterName";
import { ToggleFiltersPill } from "@faire/deprecated-retailer-visitor-shared/components/Filters/ToggleFiltersPill";
import { useFilterStore } from "@faire/deprecated-retailer-visitor-shared/components/Filters/useFilterStore";
import { handleKeyDown } from "@faire/deprecated-retailer-visitor-shared/lib/handleKeyDown";
import { Core } from "@faire/slate/foundation/color";
import { Filter as FilterIcon } from "@faire/slate/icons/Filter";
import { trackRetailersFilterInteraction } from "@faire/web-api--source/events/retailers/actionCategoryOther/filterInteraction";
import { trackRetailersFilterClearClick } from "@faire/web-api--source/events/retailers/click/filterClear";
import { useStrictLocalization } from "@faire/web/common/localization";
import { Button } from "@faire/web/slate-deprecated/Button";
import { Row } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { getSpacing } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React from "react";
import styled from "styled-components";

export const MobileToggleFiltersPill = ({ name }: { name: FilterName }) => {
  const {
    data: { activeFilterCount },
    setFilterState,
    clearAllFilters,
  } = useFilterStore(["activeFilterCount"]);
  const { strictLocalize } = useStrictLocalization();

  const hasFilterSelected = activeFilterCount > 0;
  const textColor = hasFilterSelected
    ? Core.text.primaryInverse
    : Core.text.primary;
  const iconColor = hasFilterSelected
    ? Core.icon.primaryInverse
    : Core.icon.primary;

  const onMobileAllFilterIconClick = () => {
    trackRetailersFilterInteraction(name, "open_filters", "modal");
    setFilterState((currentState) => ({
      ...currentState,
      isMobileModalOpen: true,
      isNewFilterSelected: false,
    }));
  };

  const handleClearFilters = () => {
    if (activeFilterCount > 0) {
      trackRetailersFilterClearClick(name, "mobile_clear_all", "");
      clearAllFilters();
    }
  };

  return (
    <MobileButtonsContainer>
      {hasFilterSelected ? (
        <StyledClearButton variant="tertiary" onClick={handleClearFilters}>
          <StrictLocalMsg
            id="M+I5uz"
            defaultMessage="Clear all"
            description={{ text: "Remove all applied filters" }}
          />
        </StyledClearButton>
      ) : null}
      {activeFilterCount > -1 ? (
        <ToggleFiltersPill
          isOpen={false}
          alwaysShowCloseButton={hasFilterSelected}
          hasFilterSelected={true}
          onClick={onMobileAllFilterIconClick}
          role="button"
          data-test-id="open-filters-modal-button"
          aria-label={strictLocalize({
            id: "bA3RRW",
            defaultMessage: "Open filters modal",
            description: {
              text: "accessible text to open the filters modal",
            },
          })}
          tabIndex={0}
          onKeyDown={handleKeyDown(onMobileAllFilterIconClick)}
          $showAppliedState={hasFilterSelected}
        >
          <Spacer width={4} />
          <Typography style={{ width: "max-content" }} color={textColor}>
            {hasFilterSelected ? (
              <StrictLocalMsg
                id="JnXG/8"
                defaultMessage="Filters ({activeFilterNumber})"
                description={{
                  text: "Show the number of active filters to the user",
                }}
                values={{
                  activeFilterNumber: activeFilterCount,
                }}
              />
            ) : (
              <StrictLocalMsg
                id="aUieV5"
                defaultMessage="All filters"
                description={{
                  text: "Narrow down the search (by applying filters)",
                }}
              />
            )}
          </Typography>
          <Spacer width={4} />
          <FilterIcon fontSize="16px" color={iconColor} />
        </ToggleFiltersPill>
      ) : null}
      <Spacer width="1x" />
    </MobileButtonsContainer>
  );
};

// eslint-disable-next-line @faire/prefer-slate-attrs -- use ResponsiveContentV2
const MobileButtonsContainer = styled(Row)`
  min-width: max-content;
  padding: ${getSpacing("1x", 0)};

  &&& {
    ${responsiveMediaQueries.desktopAndAbove} {
      display: none;
    }
  }
`;

const StyledClearButton = styled(Button)`
  margin-right: ${getSpacing("2x")};
  min-width: max-content;
`;
